import { useCallback, useMemo, useReducer, useState } from 'react'
import {
  isCoachDraft,
  isPublishedCoachComment,
} from '@/lib/coaching/review-comment-visibility'
import {
  MOCK_BEHAVIORAL_REVIEW,
  MOCK_CODING_APPROACHES,
  MOCK_CODING_REVIEW,
  MOCK_PRACTICE_SESSION,
  MOCK_REVIEW_WEEKS,
  MOCK_SD_REVIEW,
} from '../_mock-data'
import type { KindCounts, TypeCounts } from '../store'
import {
  countByKind,
  countByQuestionType,
  sortByWorkedAt,
} from './_lib/live-review'
import type {
  ReviewApproach,
  ReviewComment,
  ReviewSubmission,
  ReviewWeekGroup,
} from './contracts'
import type { ReviewSlice } from './review-slice'
import { useReviewUiState, useWeekFolds } from './review-ui-state'

/**
 * Fixture-backed review lane (spec §8.3/§8.5 seeds). This is what
 * `useReviewSlice` was before LG-503 wired the real slice to
 * `coachingV2.reviews.*`; it stays because the mock shell — the whole
 * dashboard rendered with no tRPC provider, which most component tests and
 * the design walkthrough rely on — still needs a Review tab with data in it.
 *
 * Everything here is in-memory: approving a comment flips it in the seed
 * queue, and the counters derive live off that queue (no independently
 * maintained totals, spec §8.8). The lifecycle commands have nothing to talk
 * to, so they resolve without doing anything.
 */

// Last week has no fixture history to derive from — these are the §6.3 seeds.
const LAST_WEEK_KIND_COUNTS: KindCounts = {
  praise: 7,
  critique: 4,
  suggestion: 2,
}
const LAST_WEEK_TYPE_COUNTS: TypeCounts = {
  coding: 4,
  sd: 4,
  behavioral: 3,
  task: 2,
}

interface State {
  queue: ReviewSubmission[]
  nextCommentSeq: number
}

type Action =
  | { type: 'approve'; id: string }
  | { type: 'markPosted'; id: string }
  | { type: 'dismiss'; id: string }
  | { type: 'rewrite'; id: string }
  | { type: 'editBody'; id: string; body: string }
  | {
      type: 'post'
      submissionId: string
      comment: Omit<ReviewComment, 'id' | 'status' | 'source'>
    }
  | { type: 'publish'; submissionId: string }
  | { type: 'unpublish'; submissionId: string }

function mapComment(
  queue: ReviewSubmission[],
  id: string,
  fn: (c: ReviewComment) => ReviewComment,
): ReviewSubmission[] {
  return queue.map((submission) => ({
    ...submission,
    comments: submission.comments.map((c) => (c.id === id ? fn(c) : c)),
  }))
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'approve':
      return {
        ...state,
        queue: mapComment(state.queue, action.id, (c) => ({
          ...c,
          status: 'approved',
        })),
      }
    case 'markPosted':
      return {
        ...state,
        queue: mapComment(state.queue, action.id, (c) => ({
          ...c,
          status: 'posted',
        })),
      }
    case 'dismiss':
      return {
        ...state,
        queue: mapComment(state.queue, action.id, (c) => ({
          ...c,
          status: 'dismissed',
        })),
      }
    case 'rewrite':
      return {
        ...state,
        queue: mapComment(state.queue, action.id, (c) => ({
          ...c,
          body: c.alt ?? c.body,
        })),
      }
    case 'editBody':
      return {
        ...state,
        queue: mapComment(state.queue, action.id, (c) => ({
          ...c,
          body: action.body,
        })),
      }
    case 'publish':
    case 'unpublish': {
      // The fixture lane's half of LG-641: a coach comment is written as a
      // draft and only the review's publish control moves it, so the mock
      // shell shows the same unreleased-work marking the live tab does.
      const moves =
        action.type === 'publish' ? isCoachDraft : isPublishedCoachComment
      const status = action.type === 'publish' ? 'posted' : 'pending'
      return {
        ...state,
        queue: state.queue.map((submission) =>
          submission.id === action.submissionId
            ? {
                ...submission,
                comments: submission.comments.map((c) =>
                  moves(c) ? { ...c, status } : c,
                ),
              }
            : submission,
        ),
      }
    }
    case 'post': {
      const comment: ReviewComment = {
        ...action.comment,
        id: `coach-${state.nextCommentSeq}`,
        source: 'coach',
        status: 'pending',
      }
      return {
        ...state,
        nextCommentSeq: state.nextCommentSeq + 1,
        queue: state.queue.map((submission) =>
          submission.id === action.submissionId
            ? { ...submission, comments: [...submission.comments, comment] }
            : submission,
        ),
      }
    }
    default: {
      // Not `return state`: a new `Action` member has to be handled here, and
      // a silent no-op is how the fixture lane would drift out of step with
      // the `ReviewSlice` contract without a compile error.
      const unhandled: never = action
      return unhandled
    }
  }
}

function initState(): State {
  return {
    // Spec §8.3 seeds: LIS (coding, expanded by default), URL Shortener
    // (system design), the disagreed-story question (behavioral) — plus the
    // LG-646 practice session, worked and never submitted.
    queue: [
      MOCK_CODING_REVIEW,
      MOCK_SD_REVIEW,
      MOCK_BEHAVIORAL_REVIEW,
      MOCK_PRACTICE_SESSION,
    ],
    nextCommentSeq: 1,
  }
}

/**
 * Which fixture week each seed was worked in. Stated rather than derived from
 * `workedAtMs`: the live path buckets in SQL, in the engagement's own time
 * zone, and a fixture that re-derived it in the browser would be asserting a
 * rule this layer does not own.
 */
const MOCK_WEEK_BY_SUBMISSION: Partial<Record<string, string>> = {
  [MOCK_CODING_REVIEW.id]: 'week-3',
  [MOCK_SD_REVIEW.id]: 'week-3',
  [MOCK_PRACTICE_SESSION.id]: 'week-3',
  [MOCK_BEHAVIORAL_REVIEW.id]: 'week-2',
}

const READY = { isLoading: false, error: null }
const EMPTY_APPROACH_IDS: ReadonlySet<string> = new Set()

export function useMockReviewSlice(): ReviewSlice {
  const [state, dispatch] = useReducer(reducer, undefined, initState)
  const weekKeyOf = useCallback(
    (submissionId: string): string | null =>
      MOCK_WEEK_BY_SUBMISSION[submissionId] ?? null,
    [],
  )
  // The newest week opens by default, exactly as the live spine's does — so
  // the shell demonstrates the fold that keeps an older week's rows unread.
  const folds = useWeekFolds(MOCK_REVIEW_WEEKS)
  const ui = useReviewUiState(
    MOCK_CODING_REVIEW.id,
    weekKeyOf,
    folds.isWeekCollapsed,
  )
  const { expandedId } = ui
  const weeks = useMemo<ReviewWeekGroup[]>(
    () =>
      MOCK_REVIEW_WEEKS.map((week) => ({
        week,
        submissions: sortByWorkedAt(
          state.queue.filter(
            (row) => MOCK_WEEK_BY_SUBMISSION[row.id] === week.id,
          ),
        ),
        // The fixture holds every row from the first render, so a section here
        // can be neither pending nor failed, and nothing caps it.
        isLoading: false,
        error: null,
        isTruncated: false,
      })),
    [state.queue],
  )

  // Every row in an *open* week, which is what `ReviewSlice.queue` promises:
  // the live slice reads a week's rows only when the coach expands it, so the
  // derived counters below must not see a folded week's work either.
  const { isWeekCollapsed } = folds
  const queue = useMemo(
    () =>
      weeks.flatMap((group) =>
        isWeekCollapsed(group.week.id) ? [] : group.submissions,
      ),
    [isWeekCollapsed, weeks],
  )

  const comments = useMemo(
    () => queue.flatMap((submission) => submission.comments),
    [queue],
  )

  const post = useCallback(
    (comment: Omit<ReviewComment, 'id' | 'status' | 'source'>) => {
      if (expandedId === null) return
      dispatch({ type: 'post', submissionId: expandedId, comment })
    },
    [expandedId],
  )

  const noop = useCallback(async (): Promise<void> => {}, [])

  const publish = useCallback(async (submissionId: string): Promise<void> => {
    dispatch({ type: 'publish', submissionId })
  }, [])
  const unpublish = useCallback(
    async (
      submissionId: string,
      confirm: (clientLastViewedWhen: string | null | undefined) => boolean,
    ): Promise<void> => {
      // `undefined`: no receipt sits behind a fixture row, and the fixture
      // lane says so rather than claiming the client has never looked.
      if (!confirm(undefined)) return
      dispatch({ type: 'unpublish', submissionId })
    },
    [],
  )

  // In-memory, like everything else here — there is no `ReviewAttachment`
  // table behind the mock shell. Held as a set of approach ids rather than
  // the live slice's approachId→attachmentId map because the fixture has no
  // attachment ids to map to; both satisfy `attachedApproachIdsFor`.
  const [attachedApproachIds, setAttachedApproachIds] = useState<
    ReadonlySet<string>
  >(() => new Set())
  const attachedApproachIdsFor = useCallback(
    (submissionId: string): ReadonlySet<string> =>
      submissionId === expandedId ? attachedApproachIds : EMPTY_APPROACH_IDS,
    [attachedApproachIds, expandedId],
  )
  const toggleApproachAttachment = useCallback(
    // Async to match the live slice's contract, which hands back the write's
    // promise. The fixture has no write to wait on, so it resolves at once.
    async (submissionId: string, approach: ReviewApproach): Promise<void> => {
      if (submissionId !== expandedId) return
      setAttachedApproachIds((current) => {
        const next = new Set(current)
        if (!next.delete(approach.approachId)) next.add(approach.approachId)
        return next
      })
    },
    [expandedId],
  )

  return {
    feedbackMix: {
      thisWeek: countByKind(comments),
      lastWeek: LAST_WEEK_KIND_COUNTS,
    },
    byQuestionType: {
      thisWeek: countByQuestionType(queue),
      lastWeek: LAST_WEEK_TYPE_COUNTS,
    },
    awaitingCount: MOCK_REVIEW_WEEKS.reduce(
      (total, week) => total + week.awaitingCount,
      0,
    ),

    weeks,
    queue,
    expandedId,
    setExpandedId: ui.setExpandedId,
    isWeekCollapsed: folds.isWeekCollapsed,
    toggleWeekCollapsed: folds.toggleWeekCollapsed,

    comments,
    commentsFor: (submissionId) =>
      (queue.find((s) => s.id === submissionId)?.comments ?? []).filter(
        (c) => c.status !== 'dismissed',
      ),
    // Only the coding seed has a problem behind it, so only it has authored
    // approaches — the system-design and behavioral cards render no section,
    // exactly as the live path does for a card with no problem.
    approachesFor: (submissionId) =>
      submissionId === MOCK_CODING_REVIEW.id ? MOCK_CODING_APPROACHES : [],
    // Nothing to fail: the fixture lane has no read behind it.
    approachesErrorFor: () => null,
    attachedApproachIdsFor,
    // The fixture shell has its attach state in hand from the first render,
    // so the read can never be pending or failed here.
    attachmentsErrorFor: () => null,
    toggleApproachAttachment,
    approve: (id) => dispatch({ type: 'approve', id }),
    markPosted: (id) => dispatch({ type: 'markPosted', id }),
    dismiss: (id) => dispatch({ type: 'dismiss', id }),
    rewrite: (id) => dispatch({ type: 'rewrite', id }),
    editBody: (id, body) => dispatch({ type: 'editBody', id, body }),
    // Editing IS the save here: the fixture lane keeps one body per comment,
    // with no stored text for a local draft to sit on top of.
    saveEdit: noop,
    post,

    claim: noop,
    finish: noop,
    reopen: noop,
    // The fixture lane has no assignment to create and no producer to run:
    // pulling practice in is a write, and this shell has no write behind it.
    pull: noop,
    publish,
    unpublish,

    composer: ui.composer,
    openComposer: ui.openComposer,
    openLineComposer: ui.openLineComposer,
    closeComposer: ui.closeComposer,
    extendComposerLines: ui.extendComposerLines,
    setComposerKind: ui.setComposerKind,
    setComposerDraft: ui.setComposerDraft,
    overallDrafts: ui.overallDrafts,
    setOverallDraft: ui.setOverallDraft,

    attachments: ui.attachments,
    toggleAttachment: ui.toggleAttachment,

    status: READY,
  }
}
